import { scrollTriggerIntoView } from '../../util/modalUtils';
import React, { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Table, Modal, Dropdown, Menu, Select, DatePicker, Spin } from "antd";
import html2pdf from "html2pdf.js/dist/html2pdf.min.js";
import { usePolicyTransactionsByNumber } from "../../hooks/products/life/usePolicyTransactionsByNumber";
import TransactionStatement from "../../components/TransactionStatement";
// Make sure this is imported or defined
// const CustomPagination = ... (defined at the end of the file in the original code) 
import LinkSuccessModal from "../dashboard/LinkSuccessModal";
// You need to ensure CustomPagination is available or defined here.
// Re-including CustomPagination for completeness.
function CustomPagination({ current, pageSize, total, onChange }) {
  const pageCount = Math.ceil(total / pageSize);
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
  const navBtnStyle = {
    background: 'transparent',
    color: '#184b18',
    border: 'none',
    borderRadius: 0,
    padding: '0 10px',
    minWidth: 0,
    height: 26,
    fontWeight: 700,
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s',
    boxShadow: 'none',
    margin: '0 2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
  };
  const navBtnDisabledStyle = {
    ...navBtnStyle,
    color: '#bdbdbd',
    cursor: 'not-allowed',
    opacity: 0.6,
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button
        onClick={() => current > 1 && onChange(current - 1)}
        disabled={current === 1}
        style={current === 1 ? navBtnDisabledStyle : navBtnStyle}
        aria-label="Previous page"
      >
        {'<'}
      </button>
      {pages.map(page => (
        <button
          key={page}
          onClick={() => onChange(page)}
          style={{
            border: '0.5px solid #184b18',
            background: page === current ? 'linear-gradient(90deg, #05b005d7 0%, #00b050 60%, #90ee90 100%)' : 'transparent',
            color: page === current ? '#fff' : '#184b18',
            borderRadius: 18,
            padding: '0 14px',
            minWidth: 36,
            height: 36,
            fontWeight: 700,
            fontSize: 12,
            outline: 'none',
            cursor: 'pointer',
            transition: 'background 0.2s, color 0.2s',
            boxShadow: 'none',
            margin: '0 2px',
          }}
        >
          {page}
        </button>
      ))}
      <button
        onClick={() => current < pageCount && onChange(current + 1)}
        disabled={current === pageCount}
        style={current === pageCount ? navBtnDisabledStyle : navBtnStyle}
        aria-label="Next page"
      >
        {'>'}
      </button>
    </div>
  );
}
// End of CustomPagination definition

export default function LifeTransactionsHistory({ transactions: propTransactions = [], pagination: propPagination = {} }) {
  const location = useLocation();
  const policyNumber = location.state?.productDetails?.policyNumber;
  // NOTE: You need to make sure usePolicyTransactionsByNumber is not causing issues 
  // in a real environment if it's async and data is null initially.
  const { data: transactionsData, isLoading: transactionsLoading } = usePolicyTransactionsByNumber(policyNumber, !!policyNumber);
  const transactions = transactionsData?.data || propTransactions;
  const pagination = transactionsData?.pagination || propPagination;
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = pagination.page_size || 10;
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [showDownloadSuccessModal, setShowDownloadSuccessModal] = useState(false);
  const [statementYear, setStatementYear] = useState([]);
  const [statementError, setStatementError] = useState("");
  const statementPdfRef = useRef();
  const searchInputRef = useRef(null);

  // Normalize transactions for table
  const normalizedTransactions = transactions.map(item => ({
    date: item.payment_date || item.created_on || item.date || "",
    transaction: item.trans_type || item.transaction || item.comment || "",
    reference: item.receipt_number || item.reference || item.id || "",
    amount: typeof item.received !== "undefined" ? item.received : (typeof item.amount !== "undefined" ? item.amount : 0),
    narration: item.narration || ""
  }));

  // Sort by date descending
  const sortedTransactions = [...normalizedTransactions].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Statement filter logic (omitting for brevity, as it's not the focus)
  const statementFilteredTransactions = React.useMemo(() => {
    if (!statementYear || statementYear.length === 0) return [];
    if (statementYear.includes("all")) return transactions;
    const yearsSet = new Set(statementYear.map(y => Number(y)));
    return transactions.filter(t => {
      if (!t || !t.payment_date) return false;
      const d = new Date(t.payment_date);
      if (Number.isNaN(d.getTime())) return false;
      return yearsSet.has(d.getFullYear());
    });
  }, [transactions, statementYear]);

  // Prepare rows and columns for statement (omitting for brevity)
  const statementRows = statementFilteredTransactions.map(item => ({
    date: item.payment_date || item.created_on || item.date || "",
    type: item.trans_type || item.transaction || item.comment || "",
    reference: item.receipt_number || item.reference || item.id || "",
    amount: typeof item.received !== "undefined" ? item.received : (typeof item.amount !== "undefined" ? item.amount : 0)
  }));

  const statementColumns = [
    {
      title: "DATE",
      dataIndex: "date",
      key: "date",
      widthStyle: { width: '18%', borderRight: '1px solid #888', textAlign: 'center' },
      render: (date) => date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
    },
    {
      title: "TRANSACTION TYPE",
      dataIndex: "type",
      key: "type",
      widthStyle: { width: '26%', borderRight: '1px solid #888', textAlign: 'center' }
    },
    {
      title: "REFERENCE",
      dataIndex: "reference",
      key: "reference",
      widthStyle: { width: '38%', borderRight: '1px solid #888', textAlign: 'center' }
    },
    {
      title: "AMOUNT",
      dataIndex: "amount",
      key: "amount",
      widthStyle: { width: '18%', borderRight: 'none', textAlign: 'center' },
      render: (amount) => `GHS ${Math.abs(Number(amount)).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    }
  ];

  // Table columns
  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      align: "center",
      render: date => <div style={{ textAlign: "left", paddingLeft: 8 }}>{formatDate(date)}</div>
    },
    {
      title: "Transaction",
      dataIndex: "transaction",
      key: "transaction",
      align: "left"
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      align: "left"
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "center",
      // Important for readability on small screens
      render: amount => <span style={{ color: "#111", fontWeight: 600, whiteSpace: 'nowrap' }}>GHS {Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
    }
  ];

  // Filtering logic (omitting for brevity)
  let filteredTransactions = sortedTransactions;
  const isDateFilterActive = !!dateFilter;
  const isSearchActive = !!searchValue;
  if (isDateFilterActive && !isSearchActive) {
    let start, end;
    const today = new Date();
    if (dateFilter === "week") {
      end = today;
      start = new Date();
      start.setDate(today.getDate() - 6);
    } else if (dateFilter === "month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = today;
    } else if (dateFilter === "year") {
      start = new Date(today.getFullYear(), 0, 1);
      end = today;
    } else if (dateFilter === "custom" && Array.isArray(customDateRange) && customDateRange[0] && customDateRange[1]) {
      start = customDateRange[0].toDate ? customDateRange[0].toDate() : customDateRange[0];
      end = customDateRange[1].toDate ? customDateRange[1].toDate() : customDateRange[1];
    }
    if (start && end) {
      filteredTransactions = filteredTransactions.filter(t => {
        const d = new Date(t.date);
        // Normalize time for comparison
        d.setHours(0, 0, 0, 0);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return d >= start && d <= end;
      });
    }
  } else if (isSearchActive) {
    const val = searchValue.trim().toLowerCase();
    filteredTransactions = filteredTransactions.filter(t =>
      t.reference.toLowerCase().includes(val) ||
      t.amount.toString().includes(val)
    );
  }

  // Pagination
  const paginatedTransactions = React.useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredTransactions.slice(startIdx, startIdx + pageSize);
  }, [filteredTransactions, currentPage, pageSize]);

  // Helpers
  function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    // Use an abbreviated format for mobile
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  }
  function getLast5Years() {
    const years = [];
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 5; i++) years.push(currentYear - i);
    return years;
  }

  // Statement modal logic (omitting for brevity)
  const [isStatementGenerating, setIsStatementGenerating] = useState(false);
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  function handleStatementDownload() {
    if (!statementYear || statementYear.length === 0) {
      setStatementError("Please select at least one year or All Years");
      return;
    }
    setStatementError("");
    setModalOpen(false);
    setIsStatementGenerating(true);
    setTimeout(() => {
      setShowStatementModal(true);
      setIsStatementGenerating(false);
    }, 300);
  }
  function handlePdfDownload() {
    if (statementPdfRef.current) {
      const node = statementPdfRef.current.querySelector('.pdf-page') || statementPdfRef.current;
      const prevWidth = node.style.width;
      (async () => {
        try {
          setIsPdfDownloading(true);
          // Force table/summary visible for PDF generation on mobile
          node.classList.add('force-pdf-visible');
          node.style.width = '210mm';
          await new Promise(r => setTimeout(r, 60));
          const imgs = node.querySelectorAll('img');
          await Promise.all(Array.from(imgs).map(img => (img.complete ? Promise.resolve() : new Promise(resolve => { img.onload = img.onerror = resolve; }))));

          await html2pdf()
            .set({
              margin: 10,
              filename: `Transaction_Statement.pdf`,
              image: { type: 'jpeg', quality: 0.98 },
              html2canvas: { scale: 2, useCORS: true, logging: false },
              jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
              pagebreak: { mode: ['css', 'legacy'] }
            })
            .from(node)
            .save();

          setShowDownloadSuccessModal(true);
        } catch (err) {
          console.error('Failed to generate PDF', err);
        } finally {
          node.classList.remove('force-pdf-visible');
          node.style.width = prevWidth;
          setIsPdfDownloading(false);
        }
      })();
    }
  }
  // Ensure statement-related modals are scrolled into view when opened
  React.useEffect(() => {
    if (showStatementModal || showDownloadSuccessModal) scrollTriggerIntoView();
  }, [showStatementModal, showDownloadSuccessModal]);
  function handleSearchClick() {
    // Keep this logic - it's good for mobile toggling
    setShowSearch(prev => !prev);
    setTimeout(() => {
      if (!showSearch && searchInputRef.current) searchInputRef.current.focus();
    }, 200);
  }
  function handleDateMenuClick({ key }) {
    if (isSearchActive) return;
    setDateFilter(key);
    if (key === "custom") {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
      setCustomDateRange([null, null]);
    }
  }

  // Date filter menu
  const dateMenu = (
    <Menu onClick={handleDateMenuClick} selectedKeys={[dateFilter]} style={{ minWidth: 160 }}>
      <Menu.Item key="week" style={{ fontWeight: 500 }} disabled={isSearchActive}>This Week</Menu.Item>
      <Menu.Item key="month" style={{ fontWeight: 500 }} disabled={isSearchActive}>This Month</Menu.Item>
      <Menu.Item key="year" style={{ fontWeight: 500 }} disabled={isSearchActive}>This Year</Menu.Item>
      <Menu.Item key="custom" style={{ fontWeight: 500 }} disabled={isSearchActive}>Custom</Menu.Item>
    </Menu>
  );

  // UI
  return (
    <div>
    <LinkSuccessModal
             visible={showDownloadSuccessModal}
             onClose={() => setShowDownloadSuccessModal(false)}
             title="Download Successful"
             subTitle="Your transaction statement PDF has been downloaded."
             closeText="OK"
           />
      <div className="container p-0 transactions-page">
       
         <div className="py-4 d-flex flex-column align-items-center">
           {/* HEADER/FILTERS ROW - Aligned with table width */}
           <div className="mb-3 w-100" style={{ maxWidth: 1330, paddingLeft: 12, paddingRight: 12 }}>
             <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 16 }}>
               <div className="mb-2 mb-sm-0">
                 <div className="page-title">Life Transactions</div>
               </div>
               
               <div className="d-flex flex-wrap align-items-center controls-container" style={{ gap: 12 }}>
                 {/* Search Input */}
                 <div className="search-container" style={{ position: 'relative', display: 'flex', alignItems: 'center', borderRadius: 24, background: '#fff', boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)', padding: '4px 4px 4px 12px', transition: 'all .28s ease', width: showSearch ? 280 : 120, minWidth: showSearch ? 120 : 120 }}>
                   <i
                     className="bi bi-search"
                     style={{ fontSize: 18, color: '#666', cursor: 'pointer', zIndex: 2, marginRight: 8, flexShrink: 0 }}
                     aria-label="Search"
                     onClick={handleSearchClick}
                     tabIndex={0}
                     onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleSearchClick(); }}
                     role="button"
                   ></i>
                   <input
                     ref={searchInputRef}
                     type="text"
                     placeholder={showSearch ? "Search by ref/amount..." : "Search"}
                     className="form-control border-0"
                     style={{ background: 'transparent', border: 'none', boxShadow: 'none', textAlign: 'left', padding: '8px 0', width: showSearch ? 200 : 60, opacity: 1, transition: 'width .28s ease' }}
                     value={searchValue}
                     onChange={e => setSearchValue(e.target.value)}
                    onBlur={() => { if (!searchValue) setShowSearch(false); }} 
                    disabled={isDateFilterActive}
                   />
                 </div>

                 {/* Date Filter Dropdown */}
                 <div className="controls-left d-flex align-items-center" style={{ gap: 12 }}>
                   <Dropdown overlay={dateMenu} trigger={["click"]} placement="bottomRight">
                     <button className="btn btn-light border-0 d-flex align-items-center justify-content-center date-filter-btn" style={{ borderRadius: 24, height: 36, background: 'transparent', fontWeight: 500, display: 'flex', alignItems: 'center', padding: '0 12px' }} title="Filter by date" disabled={isSearchActive}>
                       <span className="d-none d-sm-inline" style={{ whiteSpace: 'nowrap', marginRight: 8 }}>Date</span>
                       <i className="bi bi-calendar" style={{ fontSize: 20 }}></i>
                     </button>
                   </Dropdown>
                  </div>
                </div>
              </div>
            </div>
          
          {/* Custom Date Range Picker - Adjusted its positioning for better flow */}
          {showCustomDatePicker && (
            <div className="w-100 px-3 pb-3 custom-date-picker-row">
              <div className="d-flex justify-content-end">
                <DatePicker.RangePicker
                  value={Array.isArray(customDateRange) ? customDateRange : [null, null]}
                  onChange={vals => {
                    if (!vals || !vals[0] || !vals[1]) {
                      setCustomDateRange([null, null]);
                      setDateFilter("");
                      setShowCustomDatePicker(false);
                    } else {
                      setCustomDateRange(vals);
                      setDateFilter('custom');
                    }
                  }}
                  allowClear
                  style={{ minWidth: 220, marginBottom: 0, maxWidth: '100%' }}
                  disabled={isSearchActive}
                  format="YYYY-MM-DD"
                />
              </div>
            </div>
          )}

          {/* Generate Statement Modal */}
          <Modal
            open={modalOpen}
            onCancel={() => { setModalOpen(false); setStatementYear(''); setStatementError(''); }}
            footer={null}
            centered
            closable={false}
            // ... (Title and Body styles kept the same as they are fairly responsive)
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span className="fw-bold">Generate Statement</span>
                <button
                  onClick={() => { setModalOpen(false); setStatementYear(''); setStatementError(''); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 26,
                    color: '#888',
                    cursor: 'pointer',
                    position: 'absolute',
                    top: 16,
                    right: 24,
                    zIndex: 10
                  }}
                  aria-label="Close"
                >×</button>
              </div>
            }
            bodyStyle={{ padding: '18px 24px 18px 24px', minHeight: 180, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative' }}
          >
            <div style={{ marginBottom: 16 }}>
              <p style={{ marginBottom: 16, fontSize: 13 }}>Select the consecutive years you want to generate a statement for</p>
              <div className="mb-3">
                <div className="mt-3">
                  <label className="form-label fw-bold" style={{ display: 'block', marginBottom: 10, fontSize: 15 }}>Select Year(s)</label>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Select Year(s)"
                    value={statementYear}
                    onChange={setStatementYear}
                    style={{ width: '100%', minWidth: 220, marginBottom: 12 }}
                    optionLabelProp="label"
                  >
                    <Select.Option value="all" label="All Years">All Years</Select.Option>
                    {getLast5Years().map(year => (
                      <Select.Option key={year} value={year} label={year}>{year}</Select.Option>
                    ))}
                  </Select>
                </div>
                {statementError && <div className="text-danger mt-2">{statementError}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <button className="btn btn-primary mb-4 w-80 mobile-full-width" disabled={isStatementGenerating} onClick={handleStatementDownload}>{isStatementGenerating ? <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}><Spin size="small" /> Generating...</span> : 'Preview & Download PDF'}</button>
            </div>
          </Modal>

          {/* Statement PDF Preview Modal */}
          <Modal
            open={showStatementModal}
            onCancel={() => setShowStatementModal(false)}
            footer={null}
            centered
            width={'min(1100px, calc(100vw - 32px))'}
            style={{ maxWidth: '100%' }}
            zIndex={4000}
            title={<div className="fw-bold">Transaction Statement Preview</div>}
            bodyStyle={{ padding: 0, background: '#ffffffff', maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}
          >
            <div style={{ padding: 24, background: '#ffffffff' }}>
              {/* Desktop: Full preview */}
              <div className="desktop-preview" style={{ position: 'relative' }}>
                <div ref={statementPdfRef}>
                  <TransactionStatement rows={statementRows} columns={statementColumns} />
                </div>
                {isPdfDownloading && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', zIndex: 40 }}>
                    <div style={{ textAlign: 'center' }}><Spin size="large" /><div style={{ marginTop: 8 }}>Preparing PDF...</div></div>
                  </div>
                )}
              </div>
              {/* Mobile: Simple view with file icon and download button */}
              <div className="mobile-preview-simple" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', padding: '32px 20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                  <i className="bi bi-file-earmark-pdf" style={{ fontSize: 48, color: '#dc3545', marginBottom: 12 }}></i>
                  <p style={{ margin: '0 0 20px', color: '#666', fontSize: 14, textAlign: 'center', lineHeight: 1.4 }}>Transaction Statement<br/>PDF</p>
                  <button 
                    className="btn btn-primary" 
                    disabled={isPdfDownloading} 
                    onClick={handlePdfDownload}
                    style={{ width: '100%', maxWidth: 240 }}
                  >
                  {isPdfDownloading ? (
                    <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                      <Spin size="small" /> Downloading...
                    </span>
                  ) : 'Download As PDF'}
                </button>
                </div>
              </div>
              <div className="desktop-download-btn" style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                <button className="btn btn-primary mb-4 w-80 mobile-full-width" disabled={isPdfDownloading} onClick={handlePdfDownload}>{isPdfDownloading ? <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}><Spin size="small" /> Downloading...</span> : 'Download As PDF'}</button>
              </div>
            </div>
          </Modal>

          {/* Component-level responsive styles - ENHANCED */}
          <style>{`
            .transactions-page .page-title { font-weight:700; color:#111; font-size: clamp(18px, 2.2vw, 22px); }
            .transactions-page .search-container { border-radius: 24px; background: #fff; box-shadow: 0 2px 8px 0 rgba(0,0,0,0.08); overflow: hidden; }
            .transactions-page .search-field { border-radius: 24px; padding: 8px 12px; height: 36px; }
            .transactions-page .generate-btn { border-radius: 24px; height: 36px; font-weight: 600; min-width: 120px; }
            .transactions-page .card { width: 97%; max-width: 1330px; min-width: 0; }
            .transactions-page .ant-table-wrapper { overflow-x: auto; }
            .transactions-page .ant-table-body { overflow-x: auto !important; }
            .transactions-page .ant-table tbody td { font-size: clamp(12px, 1.8vw, 14px); }
            .transactions-page .ant-table thead th { font-size: clamp(12px, 1.8vw, 13px); }
            .transactions-page .mobile-full-width { width: 80%; max-width: 420px; }
            
            /* Tablet/Mid-Screen Optimization */
            @media (max-width: 900px) {
              .transactions-page .generate-btn { min-width: 96px; padding: 8px 12px; }
              .transactions-page .search-container { width: 160px !important; min-width: 160px !important; }
            }

            /* Mobile-First Optimization (Small Screens) */
            @media (max-width: 576px) {
              .transactions-page { padding: 8px; }
              .transactions-page .page-title { font-size: 16px; margin-bottom: 12px; }
              
              .transactions-page .controls-container { 
                width: 100%; 
                flex-direction: column;
                align-items: stretch;
                gap: 12px;
              }
              .transactions-page .controls-left { 
                width: 100%;
                flex-direction: column;
                align-items: stretch;
                gap: 12px;
              }
              .transactions-page .controls-left > div {
                width: 100%;
              }
              .transactions-page .controls-right { 
                width: 100%;
              }
              .transactions-page .controls-right .generate-btn {
                width: 100%;
              }

              .transactions-page .search-container { 
                width: 100% !important;
                min-width: 100% !important;
                padding: 8px 12px 8px 12px !important;
              }
              .transactions-page .search-container input {
                width: 100% !important;
              }

              .transactions-page .generate-btn { 
                min-width: unset; 
                padding: 10px 16px; 
                font-size: 13px; 
                height: 40px;
              }

              .transactions-page .card { width: 100%; min-width: 0; border-radius: 10px; }
              .transactions-page .ant-table { font-size: 11px; }
              .transactions-page .ant-table table { min-width: max-content; }
              .transactions-page .ant-table thead th,
              .transactions-page .ant-table tbody td {
                padding: 8px 4px;
                font-size: 11px;
              }
              
              .transactions-page .mobile-full-width { width: 100% !important; max-width: 100%; }

              .transactions-page .d-flex.justify-content-between.align-items-center.px-4.py-3 {
                flex-direction: column;
                gap: 12px;
                padding: 12px;
              }
            }
            
            /* Extra Small Screens */
            @media (max-width: 480px) {
              .transactions-page .page-title { font-size: 15px; }
              .transactions-page .search-container { padding: 6px 10px 6px 10px !important; }
              .transactions-page .ant-table { font-size: 10px; }
              .transactions-page .ant-table thead th,
              .transactions-page .ant-table tbody td {
                padding: 6px 2px;
                font-size: 10px;
              }
            }
          `}</style>
          {/* ... (Rest of the component structure) */}
          <div className="w-100 d-flex justify-content-center" style={{ minHeight: 120, background: 'transparent' }}>
              <div className="card p-0 shadow-sm" style={{ width: '97%', maxWidth: 1330, minWidth: 0, color: '#111', overflow: 'auto', borderRadius: 10, position: 'relative' }}>
                {transactionsLoading && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', zIndex: 20 }}>
                    <Spin size="large" />
                  </div>
                )}
                <Table
                  columns={columns}
                  dataSource={paginatedTransactions}
                  rowKey={(record, idx) => idx}
                  pagination={false}
                  locale={{ emptyText: <span className="text-muted" style={{ fontSize: 18 }}>No transactions found.</span> }}
                  style={{ overflow: 'hidden', borderRadius: 24 }}
                  scroll={{ x: 'max-content' }}
                />
              </div>
          </div>
          <div className="w-100 d-flex justify-content-center mt-2">
            <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ maxWidth: 1330, minWidth: 0, width: '100%', borderRadius: 10, border: 'none', background: 'transparent' }}>
              <div className="text-muted d-none d-sm-block" style={{ fontSize: 15, fontWeight: 500 }}>
                Showing 1-{paginatedTransactions.length} of {filteredTransactions.length} results
              </div>
              <CustomPagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredTransactions.length}
                onChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
